import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import type { DocumentNode, OperationVariables, TypedDocumentNode } from '@apollo/client';

interface UseInfiniteScrollQueryOptions<TData, TVars> {
  query: DocumentNode | TypedDocumentNode<TData, TVars>;
  // Builds the query variables for a given offset. The base query is fetched at
  // offset 0; each infinite-scroll page re-uses it with the next offset.
  buildVariables: (offset: number) => TVars;
  // Number of rows per page; the offset advances by this on each load.
  pageSize: number;
  // Skips the query entirely (e.g. term too short). Optional.
  skip?: boolean;
  // Appends a freshly fetched page onto the accumulated result. Query-shape
  // specific, so the caller supplies it.
  append: (previous: TData, next: TData) => TData;
}

// Encapsulates the offset-based infinite-scroll boilerplate shared by the
// search screens: it owns the paging offset, runs the base query, and exposes a
// `loadMore` handler wired for `IonInfiniteScroll` (fetch next page, append it,
// advance the offset and complete the event). `refetch` and `resetPagination`
// are passed through for callers that drive the search imperatively.
export function useInfiniteScrollQuery<TData, TVars extends OperationVariables>({
  query,
  buildVariables,
  pageSize,
  skip,
  append,
}: UseInfiniteScrollQueryOptions<TData, TVars>) {
  const [offset, setOffset] = useState(0);

  const { data: maskedData, loading, error, fetchMore, refetch } = useQuery<TData, TVars>(query, {
    variables: buildVariables(0),
    skip,
  });

  // Apollo types `data` as `MaybeMasked<TData>`. That conditional type does not
  // resolve against a generic `TData`, which erases field precision at the call
  // site (fields read back as possibly-undefined). We don't use fragment masking
  // here, so expose the plain `TData`.
  const data = maskedData as TData | undefined;

  const loadMore = async (event: CustomEvent<void>) => {
    const newOffset = offset + pageSize;

    try {
      await fetchMore({
        variables: buildVariables(newOffset),
        updateQuery: (previous, { fetchMoreResult }) =>
          fetchMoreResult ? append(previous, fetchMoreResult) : previous,
      });
      setOffset(newOffset);
    } catch {
      // Swallow: leave the offset unchanged so the next scroll retries the same
      // page (and avoid an unhandled rejection from the onIonInfinite handler).
    } finally {
      // Always release the infinite-scroll spinner, even when the page failed;
      // otherwise it stays busy forever and blocks further scrolling.
      (event.target as HTMLIonInfiniteScrollElement).complete();
    }
  };

  const resetPagination = () => setOffset(0);

  return { data, loading, error, loadMore, refetch, resetPagination };
}
